/**
 * Workflow Mutation Hook
 * 
 * Provides mutation capabilities for all 15 workflow types
 * Following append-only architecture with proper error handling
 */

import { useMutation } from '@ferp/graphql-client';
import { notifications } from '@mantine/notifications';
import {
  // Core Operations
  CREATE_WORKFLOW_DEFINITION,
  START_WORKFLOW_INSTANCE,
  CREATE_TASK,
  CREATE_POLICY,
  CREATE_SCHEDULE,
  CREATE_RESOURCE,
  CREATE_INTEGRATION,
  CREATE_ESCALATION,
  CREATE_CONTEXT,
  CREATE_ACTOR,
  CREATE_TRANSITION,
  CREATE_SIGNAL_TEMPLATE,
  CREATE_TIMELINE_ENTRY,
  CREATE_EXECUTION_RESULT,
  CREATE_JOB,
  // Input Types
  type CreateDraftInput,
  type WorkflowInstanceInput,
  type TaskInput,
  type PolicyInput,
  type ScheduleInput,
  type ResourceInput,
  type IntegrationInput,
  type EscalationInput
} from '@ferp/graphql-client';

export type WorkflowTypeName =
  | 'Workflow Definition'
  | 'Workflow Instance'
  | 'Task'
  | 'Schedule'
  | 'Policy'
  | 'Signal'
  | 'Resource'
  | 'Integration'
  | 'Escalation'
  | 'Actor Descriptor'
  | 'Context'
  | 'Execution Result'
  | 'Timeline Item'
  | 'Transition Catalog'
  | 'Job Status';

const MUTATION_MAP = {
  'Workflow Definition': CREATE_WORKFLOW_DEFINITION,
  'Workflow Instance': START_WORKFLOW_INSTANCE,
  'Task': CREATE_TASK,
  'Schedule': CREATE_SCHEDULE,
  'Policy': CREATE_POLICY,
  'Signal': CREATE_SIGNAL_TEMPLATE,
  'Resource': CREATE_RESOURCE,
  'Integration': CREATE_INTEGRATION,
  'Escalation': CREATE_ESCALATION,
  'Actor Descriptor': CREATE_ACTOR,
  'Context': CREATE_CONTEXT,
  'Execution Result': CREATE_EXECUTION_RESULT,
  'Timeline Item': CREATE_TIMELINE_ENTRY,
  'Transition Catalog': CREATE_TRANSITION,
  'Job Status': CREATE_JOB
};

interface MutationOptions {
  tenant?: string;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
  showNotifications?: boolean;
}

export const useWorkflowMutation = (workflowType: WorkflowTypeName) => {
  const mutation = MUTATION_MAP[workflowType];
  const [mutate, { loading, error }] = useMutation<Record<string, any>>(mutation);

  const save = async (data: any, options: MutationOptions = {}) => {
    const {
      tenant = 'test',
      onSuccess,
      onError,
      showNotifications = true
    } = options;

    try {
      const input: CreateDraftInput = {
        tenant,
        kind: workflowType,
        key: `${workflowType.toLowerCase()}-${Date.now()}`,
        draft: data,
      };

      const result = await mutate({
        variables: { input }
      });

      if (showNotifications) {
        notifications.show({
          title: 'Success',
          message: `${workflowType} created successfully`,
          color: 'green'
        });
      }

      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      if (showNotifications) {
        notifications.show({
          title: 'Error',
          message: `Failed to create ${workflowType}: ${err instanceof Error ? err.message : 'Unknown error'}`,
          color: 'red'
        });
      }

      if (onError) {
        onError(err);
      }

      throw err;
    }
  };

  return {
    save,
    loading,
    error
  };
};

// Specialized hooks for specific workflow types
export const useWorkflowDefinitionMutation = () => useWorkflowMutation('Workflow Definition');
export const useWorkflowInstanceMutation = () => useWorkflowMutation('Workflow Instance');
export const useTaskMutation = () => useWorkflowMutation('Task');
export const useScheduleMutation = () => useWorkflowMutation('Schedule');
export const usePolicyMutation = () => useWorkflowMutation('Policy');
export const useSignalMutation = () => useWorkflowMutation('Signal');
export const useResourceMutation = () => useWorkflowMutation('Resource');
export const useIntegrationMutation = () => useWorkflowMutation('Integration');
export const useEscalationMutation = () => useWorkflowMutation('Escalation');
export const useActorDescriptorMutation = () => useWorkflowMutation('Actor Descriptor');
export const useContextMutation = () => useWorkflowMutation('Context');
export const useExecutionResultMutation = () => useWorkflowMutation('Execution Result');
export const useTimelineItemMutation = () => useWorkflowMutation('Timeline Item');
export const useTransitionCatalogMutation = () => useWorkflowMutation('Transition Catalog');
export const useJobStatusMutation = () => useWorkflowMutation('Job Status');

// Helper to get proper input type based on workflow type
export const createWorkflowInput = (
  workflowType: WorkflowTypeName,
  data: any
): CreateDraftInput => {
  return {
    tenant: 'test',
    kind: workflowType,
    key: `${workflowType.toLowerCase()}-${Date.now()}`,
    draft: data,
  };
};

// Backward compatibility hooks
export const useInstanceMutation = () => useWorkflowMutation('Workflow Instance');
export const useActorMutation = () => useWorkflowMutation('Actor Descriptor');
export const useTransitionMutation = () => useWorkflowMutation('Transition Catalog');
export const useSignalTemplateMutation = () => useWorkflowMutation('Signal');
export const useTimelineMutation = () => useWorkflowMutation('Timeline Item');
export const useJobMutation = () => useWorkflowMutation('Job Status');